/** Duty substate machine (patrol → wander → passive) — thin orchestrator delegating to behavior modules. */
import { distance } from '../utils/Math';
import type { EnemyArchetypeConfig } from '../utils/Types';
import type { PathHelper } from './PathHelper';
import {
  MovementResult, ZERO_MOVEMENT,
  DutySubstate,
  DUTY_RETURN_THRESHOLD,
  DEFAULT_PATROL_DURATION_MS, DEFAULT_WANDER_DURATION_MS, DEFAULT_PATROL_RADIUS,
  DEFAULT_PASSIVE_WAKE_MIN_MS, DEFAULT_PASSIVE_WAKE_MAX_MS,
  STALL_RETRY_DELAY_MS,
} from './constants';
import { createPatrolState, patrolMovement, discoverPatrolWaypoints, type PatrolState } from './behaviors/PatrolBehavior';
import { createWanderState, wanderMovement, type WanderState } from './behaviors/WanderBehavior';
import { createReturnState, resetReturnState, returnToPost, type ReturnState } from './behaviors/ReturnToPostBehavior';
import { createInvestigateState, setInvestigateTarget as initInvestigateTarget, investigateMovement, type InvestigateState } from './behaviors/InvestigateBehavior';

/**
 * Manages duty behavior with three graduated substates:
 *   patrol → wander → passive → (wake timer) → patrol
 *
 * Patrol: pace between nearby intersections/chokepoints (active guarding).
 * Wander: random movement near post (casual).
 * Passive: stationary (true idle) with a configurable wake timer that
 *   cycles back to patrol. Wake interval is per-archetype (passiveWakeMinMs/MaxMs).
 *   Set passiveWakeMinMs to -1 to disable wake-up (e.g. conductors hold position).
 */
export class DutySystem {
  dutyX: number;
  dutyY: number;

  // Substate machine
  private _substate: DutySubstate = 'patrol';
  private substateTimer: { value: number } = { value: 0 };
  private readonly patrolDurationMs: number;
  private readonly wanderDurationMs: number;
  private readonly patrolRadius: number;
  private readonly passiveWakeMinMs: number;
  private readonly passiveWakeMaxMs: number;
  private passiveWakeTimer = 0;
  private wasReturning = false;

  // Stall-retry: when return-to-post gives up, patrol at temp position then retry original
  private originalDutyX: number | null = null;
  private originalDutyY: number | null = null;
  private stallRetryTimer = 0;

  // Behavior state (owned here, passed to behavior modules)
  private patrol: PatrolState;
  private wander: WanderState;
  private returnState: ReturnState;
  private investigate: InvestigateState;

  /** Logs generated during construction (before logger is available). Consume with drainInitLogs(). */
  private _initLogs: string[] = [];

  private readonly config: EnemyArchetypeConfig;
  private readonly groundLayer: Phaser.Tilemaps.TilemapLayer;
  private readonly pathHelper: PathHelper;

  constructor(config: EnemyArchetypeConfig, dutyX: number, dutyY: number, groundLayer: Phaser.Tilemaps.TilemapLayer, pathHelper: PathHelper) {
    this.config = config;
    this.dutyX = dutyX;
    this.dutyY = dutyY;
    this.groundLayer = groundLayer;
    this.pathHelper = pathHelper;

    this.patrolDurationMs = config.patrolDurationMs ?? DEFAULT_PATROL_DURATION_MS;
    this.wanderDurationMs = config.wanderDurationMs ?? DEFAULT_WANDER_DURATION_MS;
    this.patrolRadius = config.patrolRadius ?? DEFAULT_PATROL_RADIUS;
    this.passiveWakeMinMs = config.passiveWakeMinMs ?? DEFAULT_PASSIVE_WAKE_MIN_MS;
    this.passiveWakeMaxMs = config.passiveWakeMaxMs ?? DEFAULT_PASSIVE_WAKE_MAX_MS;

    this.patrol = createPatrolState();
    this.wander = createWanderState();
    this.returnState = createReturnState();
    this.investigate = createInvestigateState();

    this._initLogs = this.initSubstate();
  }

  /** Return and clear any logs from construction. Call once after creating DutySystem. */
  drainInitLogs(): string[] {
    const logs = this._initLogs;
    this._initLogs = [];
    return logs;
  }

  get substate(): DutySubstate { return this._substate; }
  get waypointCount(): number { return this.patrol.waypoints.length; }

  /** Return current patrol waypoints (for Trace visualization). Empty if not patrolling. */
  getPatrolWaypoints(): ReadonlyArray<{ x: number; y: number }> {
    return (this._substate === 'patrol' || this._substate === 'patrol-stalled') ? this.patrol.waypoints : [];
  }

  /** Return the current waypoint index in the patrol circuit. */
  get currentWaypointIndex(): number { return this.patrol.waypointIndex; }

  /** True when the enemy has arrived near the investigate target. */
  get investigateArrived(): boolean { return this.investigate.arrived; }
  get investigateTargetX(): number { return this.investigate.targetX; }
  get investigateTargetY(): number { return this.investigate.targetY; }

  /** Switch to investigate substate and pathfind toward a corpse. Returns the randomized stop distance (px). */
  setInvestigateTarget(x: number, y: number): number {
    this._substate = 'investigate';
    initInvestigateTarget(this.investigate, x, y);
    return this.investigate.stopDistancePx;
  }

  /** Transition to passive substate and start the wake timer. */
  private enterPassive(): void {
    this._substate = 'passive';
    if (this.passiveWakeMinMs < 0) return; // -1 = never wake
    this.passiveWakeTimer = this.passiveWakeMinMs + Math.random() * (this.passiveWakeMaxMs - this.passiveWakeMinMs);
  }

  /** Restore the original duty post after a stall-retry timer expires. */
  private restoreOriginalDutyPost(logs: string[]): void {
    if (this.originalDutyX === null) return;
    logs.push(`DUTY patrol-stalled timer expired — retrying original post (${Math.round(this.originalDutyX)},${Math.round(this.originalDutyY!)})`);
    this.dutyX = this.originalDutyX;
    this.dutyY = this.originalDutyY!;
    this.originalDutyX = null;
    this.originalDutyY = null;
    resetReturnState(this.returnState);
    this._substate = 'patrol';
    this.substateTimer.value = 0;
  }

  /** Reassign the duty post and re-discover patrol waypoints. Used when spawning far from the intended post. */
  setDutyPosition(newX: number, newY: number): string[] {
    this.originalDutyX = null;
    this.originalDutyY = null;
    this.dutyX = newX;
    this.dutyY = newY;
    this.patrol = createPatrolState();
    resetReturnState(this.returnState);
    const logs = discoverPatrolWaypoints(this.patrol, newX, newY, this.patrolRadius, this.groundLayer, this.pathHelper);
    if (this.patrol.waypoints.length === 0) {
      if (this.wanderDurationMs === 0) { this.enterPassive(); } else { this._substate = 'wander'; }
      logs.push(`DUTY setDutyPosition → ${this._substate} (no waypoints found)`);
    } else {
      this._substate = 'patrol';
      this.substateTimer.value = 0;
      logs.push(`DUTY setDutyPosition → patrol (${this.patrol.waypoints.length} waypoints) at (${Math.round(newX)},${Math.round(newY)})`);
    }
    return logs;
  }

  /** Initialize the starting substate based on config (skip patrol/wander if duration is 0). */
  private initSubstate(): string[] {
    const logs: string[] = [];
    if (this.patrolDurationMs === 0) {
      if (this.wanderDurationMs === 0) {
        this.enterPassive();
      } else {
        this._substate = 'wander';
      }
      logs.push(`DUTY init → ${this._substate} (patrol disabled)`);
    } else {
      this._substate = 'patrol';
      logs.push(...discoverPatrolWaypoints(this.patrol, this.dutyX, this.dutyY, this.patrolRadius, this.groundLayer, this.pathHelper));
      if (this.patrol.waypoints.length === 0) {
        if (this.wanderDurationMs === 0) { this.enterPassive(); } else { this._substate = 'wander'; }
        logs.push(`DUTY init → ${this._substate} (no waypoints found)`);
      } else {
        logs.push(`DUTY init → patrol (${this.patrol.waypoints.length} waypoints)`);
      }
    }
    this.substateTimer.value = 0;
    return logs;
  }

  /** Reset to patrol substate after returning from combat. Returns log messages. */
  resetToPatrol(entityX: number, entityY: number): string[] {
    const logs: string[] = [];
    // Combat clears stall — restore original duty post so enemy heads home, not back to temp
    if (this.originalDutyX !== null) {
      this.dutyX = this.originalDutyX;
      this.dutyY = this.originalDutyY!;
      this.originalDutyX = null;
      this.originalDutyY = null;
    }
    this.substateTimer.value = 0;
    this.patrol = createPatrolState();
    this.wander = createWanderState();
    if (this.patrolDurationMs === 0) {
      if (this.wanderDurationMs === 0) {
        this.enterPassive();
      } else {
        this._substate = 'wander';
      }
      logs.push(`DUTY resetToPatrol → ${this._substate} (patrol disabled) at (${Math.round(entityX)},${Math.round(entityY)})`);
    } else {
      this._substate = 'patrol';
      logs.push(...discoverPatrolWaypoints(this.patrol, entityX, entityY, this.patrolRadius, this.groundLayer, this.pathHelper));
      if (this.patrol.waypoints.length === 0) {
        if (this.wanderDurationMs === 0) { this.enterPassive(); } else { this._substate = 'wander'; }
        logs.push(`DUTY resetToPatrol → ${this._substate} (no waypoints found)`);
      } else {
        logs.push(`DUTY resetToPatrol → patrol (${this.patrol.waypoints.length} waypoints) at (${Math.round(entityX)},${Math.round(entityY)})`);
      }
    }
    return logs;
  }

  /** Reset return-to-post tracking state (called when the enemy is already near post). */
  resetReturn(): void {
    resetReturnState(this.returnState);
  }

  /**
   * Duty movement: return to post (with stuck detection), then substate behavior.
   * Returns movement + log messages.
   *
   * Return-to-post is "sticky": once an enemy starts returning, it must reach
   * within DUTY_RETURN_THRESHOLD before resuming patrol/wander. This prevents
   * oscillation at the patrol-radius boundary where enemies would alternate
   * between return-to-post and patrol each frame (causing jitter).
   *
   * Patrol enemies that naturally drift beyond DUTY_RETURN_THRESHOLD while
   * following waypoints are allowed to stay in patrol (skipReturnForPatrol),
   * but only if they weren't already returning.
   */
  movement(entityX: number, entityY: number, delta: number): { movement: MovementResult; logs: string[] } {
    const logs: string[] = [];
    const distToDuty = distance(entityX, entityY, this.dutyX, this.dutyY);

    // Patrol enemies can drift beyond DUTY_RETURN_THRESHOLD while following waypoints —
    // allow this as long as they're within patrolRadius and weren't already returning.
    // Investigate enemies are fully unleashed — they must reach their corpse target.
    const skipReturnForPatrol = !this.wasReturning
      && (this._substate === 'patrol' || this._substate === 'patrol-stalled')
      && distToDuty <= this.patrolRadius;
    const skipReturnForInvestigate = this._substate === 'investigate';
    if (!skipReturnForPatrol && !skipReturnForInvestigate && distToDuty > DUTY_RETURN_THRESHOLD) {
      this.wasReturning = true;
      const movement = returnToPost(
        this.returnState, entityX, entityY, this.dutyX, this.dutyY,
        delta, this.config.moveSpeed, this.groundLayer, this.pathHelper,
        (newX, newY) => {
          // Save original duty post on first stall; preserve it on subsequent stalls
          if (this.originalDutyX === null) {
            this.originalDutyX = this.dutyX;
            this.originalDutyY = this.dutyY;
          }
          this.dutyX = newX;
          this.dutyY = newY;
          this._substate = 'patrol-stalled';
          this.stallRetryTimer = STALL_RETRY_DELAY_MS;
          this.substateTimer.value = 0;
          this.patrol = createPatrolState();
          logs.push(...discoverPatrolWaypoints(this.patrol, newX, newY, this.patrolRadius, this.groundLayer, this.pathHelper));
          logs.push(`DUTY stalled → patrol-stalled (retry in ${STALL_RETRY_DELAY_MS / 1000}s, original post at (${Math.round(this.originalDutyX!)},${Math.round(this.originalDutyY!)}))`);
        },
        logs,
      );
      return { movement, logs };
    }

    // Arrived at post — clear return state, rediscover waypoints from current position
    if (this.wasReturning) {
      this.wasReturning = false;
      this.wander.path = null;
      // Rediscover patrol waypoints from where we actually are (not the duty post),
      // since waypoints discovered from a distant position may be unreachable.
      if (this._substate === 'patrol') {
        this.patrol = createPatrolState();
        logs.push(...discoverPatrolWaypoints(this.patrol, entityX, entityY, this.patrolRadius, this.groundLayer, this.pathHelper));
        if (this.patrol.waypoints.length === 0) {
          if (this.wanderDurationMs === 0) { this.enterPassive(); } else { this._substate = 'wander'; }
          logs.push(`DUTY arrival fallback → ${this._substate} (no waypoints at arrival)`);
        } else {
          logs.push(`DUTY arrival → patrol (${this.patrol.waypoints.length} waypoints at (${Math.round(entityX)},${Math.round(entityY)}))`);
        }
      }
    }
    this.resetReturn();

    switch (this._substate) {
      case 'patrol-stalled': {
        this.stallRetryTimer -= delta;
        if (this.stallRetryTimer <= 0) {
          this.restoreOriginalDutyPost(logs);
          return { movement: ZERO_MOVEMENT, logs };
        }
        // Same patrol movement — loop waypoints until retry timer fires
        const stallResult = patrolMovement(
          this.patrol, entityX, entityY, delta, this.config.moveSpeed,
          this.patrolDurationMs, this.substateTimer, this.pathHelper, this.groundLayer, logs,
        );
        if (stallResult.exhausted) {
          // Re-loop patrol at temp post (don't transition to wander/passive while stalled)
          this.substateTimer.value = 0;
          this.patrol = createPatrolState();
          logs.push(...discoverPatrolWaypoints(this.patrol, entityX, entityY, this.patrolRadius, this.groundLayer, this.pathHelper));
        }
        return { movement: stallResult.movement, logs };
      }
      case 'patrol': {
        const result = patrolMovement(
          this.patrol, entityX, entityY, delta, this.config.moveSpeed,
          this.patrolDurationMs, this.substateTimer, this.pathHelper, this.groundLayer, logs,
        );
        if (result.exhausted) {
          if (this.wanderDurationMs === 0) { this.enterPassive(); } else { this._substate = 'wander'; }
          this.substateTimer.value = 0;
          if (this._substate === 'wander') {
            return this.doWander(entityX, entityY, delta, logs);
          }
        }
        return { movement: result.movement, logs };
      }
      case 'wander':
        return this.doWander(entityX, entityY, delta, logs);
      case 'investigate': {
        const movement = investigateMovement(
          this.investigate, entityX, entityY, this.config.moveSpeed,
          this.groundLayer, this.pathHelper, logs,
        );
        return { movement, logs };
      }
      case 'passive': {
        // Wake timer: periodically transition back to patrol
        if (this.passiveWakeMinMs >= 0) {
          this.passiveWakeTimer -= delta;
          if (this.passiveWakeTimer <= 0) {
            logs.push('DUTY passive → patrol (wake timer)');
            this._substate = 'patrol';
            this.substateTimer.value = 0;
            this.patrol = createPatrolState();
            logs.push(...discoverPatrolWaypoints(this.patrol, entityX, entityY, this.patrolRadius, this.groundLayer, this.pathHelper));
            if (this.patrol.waypoints.length === 0) {
              if (this.wanderDurationMs === 0) { this.enterPassive(); } else { this._substate = 'wander'; }
              logs.push(`DUTY wake fallback → ${this._substate} (no waypoints)`);
            }
          }
        }
        return { movement: ZERO_MOVEMENT, logs };
      }
    }
  }

  /** Delegate to wander behavior and handle exhaustion → passive transition. */
  private doWander(entityX: number, entityY: number, delta: number, logs: string[]): { movement: MovementResult; logs: string[] } {
    const result = wanderMovement(
      this.wander, entityX, entityY, delta, this.dutyX, this.dutyY,
      this.config, this.wanderDurationMs, this.substateTimer, this.groundLayer, logs,
      this.pathHelper,
    );
    if (result.exhausted) {
      this.enterPassive();
      this.substateTimer.value = 0;
    }
    return { movement: result.movement, logs };
  }
}
